
import React, { useState, useEffect } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Card } from '@/components/ui/card';
import { useConsentManager } from './useConsentManager';
import { useIsMobile } from '@/hooks/use-mobile';
import { CookieConsentMain } from './CookieConsentMain';
import { CustomizeView } from './CustomizeView';
import '../../utils/nuclearShutdown'; // Initialize nuclear shutdown

export const CookieConsentTab = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const isMobile = useIsMobile();
  const { 
    consent, 
    updateConsent, 
    acceptAll, 
    rejectNonEssential, 
    hasUserConsented 
  } = useConsentManager();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!hasUserConsented()) {
        setIsVisible(true);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [hasUserConsented]);

  const handleAcceptAll = () => {
    acceptAll();
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    rejectNonEssential();
    setIsVisible(false);
  };

  const handleSaveCustom = () => {
    setIsVisible(false);
    setShowCustomize(false);
  };

  if (!isVisible) return null;

  return (
    <DialogPrimitive.Root open modal={false}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          className={`
            fixed z-[9999] focus-visible:outline-none
            ${isMobile 
              ? 'bottom-0 left-0 right-0 max-h-[80dvh] overflow-y-auto overscroll-contain'
              : 'bottom-6 right-6 max-w-md w-[90vw] sm:w-96'
            }
          `}
        >
          <DialogPrimitive.Title className="sr-only">
            {showCustomize ? 'Customise cookie preferences' : 'Cookie consent'}
          </DialogPrimitive.Title>
          <DialogPrimitive.Description className="sr-only">
            {showCustomize
              ? 'Choose which optional cookies and data processing you allow.'
              : 'Review the privacy notice and choose your cookie preferences.'}
          </DialogPrimitive.Description>
          <Card className={`
            border-l-4 border-l-purple-600 shadow-2xl bg-white/95 backdrop-blur-lg hover:translate-y-0 hover:shadow-2xl
            ${isMobile ? 'rounded-t-lg rounded-b-none border-l-0 border-t-4 border-t-purple-600 pb-[env(safe-area-inset-bottom,0px)]' : ''}
          `}>
            {!showCustomize ? (
              <CookieConsentMain
                onAcceptAll={handleAcceptAll}
                onRejectNonEssential={handleRejectNonEssential}
                onCustomize={() => setShowCustomize(true)}
                isMobile={isMobile}
              />
            ) : (
              <div className="p-6">
                <CustomizeView
                  consent={consent}
                  updateConsent={updateConsent}
                  onBack={() => setShowCustomize(false)}
                  onSave={handleSaveCustom}
                  isMobile={isMobile}
                />
              </div>
            )}
          </Card>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};
